import { apiClient } from '../utils/api-client';
import type {
  ImportValidationResult,
  FundMatchingData,
  ExistingInvestorConflict,
  ImportResult,
  ImportSessionStatus,
} from '../types/investor-import';

class InvestorImportService {
  private baseUrl = '/investor-import';

  async uploadFile(file: File): Promise<ImportValidationResult> {
    const formData = new FormData();
    formData.append('file', file);

    return apiClient.request<ImportValidationResult>(`${this.baseUrl}/upload`, {
      method: 'POST',
      body: formData,
    });
  }

  async matchFunds(sessionId: string): Promise<FundMatchingData[]> {
    return apiClient.request<FundMatchingData[]>(
      `${this.baseUrl}/sessions/${sessionId}/match-funds`,
      {
        method: 'POST',
      }
    );
  }

  async updateFundMatches(
    sessionId: string,
    fundMappings: FundMatchingData[]
  ): Promise<{ message: string }> {
    return apiClient.request<{ message: string }>(
      `${this.baseUrl}/sessions/${sessionId}/update-fund-matches`,
      {
        method: 'POST',
        body: JSON.stringify({
          import_session_id: sessionId,
          fund_mappings: fundMappings,
        }),
      }
    );
  }

  async detectConflicts(
    sessionId: string
  ): Promise<ExistingInvestorConflict[]> {
    return apiClient.request<ExistingInvestorConflict[]>(
      `${this.baseUrl}/sessions/${sessionId}/detect-conflicts`,
      {
        method: 'POST',
      }
    );
  }

  async resolveConflicts(
    sessionId: string,
    conflicts: ExistingInvestorConflict[]
  ): Promise<{ message: string }> {
    return apiClient.request<{ message: string }>(
      `${this.baseUrl}/sessions/${sessionId}/resolve-conflicts`,
      {
        method: 'POST',
        body: JSON.stringify({
          import_session_id: sessionId,
          conflicts: conflicts,
        }),
      }
    );
  }

  async executeImport(
    sessionId: string,
    fundMappings: FundMatchingData[],
    conflictResolutions: ExistingInvestorConflict[]
  ): Promise<ImportResult> {
    return apiClient.request<ImportResult>(
      `${this.baseUrl}/sessions/${sessionId}/execute`,
      {
        method: 'POST',
        body: JSON.stringify({
          import_session_id: sessionId,
          fund_mappings: fundMappings,
          conflict_resolutions: conflictResolutions,
          import_options: {},
        }),
      }
    );
  }

  async getSessionStatus(sessionId: string): Promise<ImportSessionStatus> {
    return apiClient.request<ImportSessionStatus>(
      `${this.baseUrl}/sessions/${sessionId}/status`,
      {
        method: 'GET',
      }
    );
  }

  async listSessions(): Promise<ImportSessionStatus[]> {
    return apiClient.request<ImportSessionStatus[]>(
      `${this.baseUrl}/sessions`,
      {
        method: 'GET',
      }
    );
  }

  async deleteSession(sessionId: string): Promise<{ message: string }> {
    return apiClient.request<{ message: string }>(
      `${this.baseUrl}/sessions/${sessionId}`,
      {
        method: 'DELETE',
      }
    );
  }
}

export const investorImportService = new InvestorImportService();
